import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight,
  Target,
  BookOpen,
  Gift,
  Search,
  AlertCircle,
  Clock
} from 'lucide-react';

interface Questionnaire {
  title: string;
  category: string;
  type: string;
  link?: string;
}

const FreeAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');

  const questionnaires: Questionnaire[] = [
    { title: 'PHQ9 (Depression)', category: 'Depression', type: 'Minimum', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/phq9' },
    { title: 'GAD7 (Anxiety)', category: 'Anxiety', type: 'Minimum', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/gad7' },
    { title: 'IAPT (Phobia Scale)', category: 'Phobia', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/iapt' },
    { title: 'Work and Social Adjustment Scale', category: 'Work', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/wasas' },
    { title: 'Empathy Quotient (EQ)', category: 'Personality', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/eq' },
    { title: 'Autism Spectrum Quotient (AQ)', category: 'Autism', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/aq' },
    { title: 'Complicated Grief Inventory (CGI)', category: 'Grief', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/cgi' },
    { title: 'Child and Adolescent Disruptive Behavior Inventory - Parent Survey (CADBI)', category: 'Child', type: 'Optional', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/cadbi' },
    { title: 'Child Acceptance and Mindfulness Measure (CAMM)', category: 'Child', type: 'Optional', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/camm' },
    { title: 'Generalized Anxiety Severity Measure Adolescent (11-17)', category: 'Anxiety', type: 'Optional', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/gasma' },
    { title: 'Anger Assessment DSM-5', category: 'Anger', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/dsm5' },
    { title: 'Major Depression Inventory (MDI)', category: 'Depression', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/mdi' },
    { title: 'MDQ: Assesses depression.', category: 'Depression', type: 'Other' },
    { title: 'Depression Anxiety Stress Scale (DASS21)', category: 'Stress', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/dass21' },
    { title: 'Edinburgh Postnatal Depression Scale (EPDS).', category: 'Depression', type: 'Other' },
    { title: 'Young Mania Rating Scale (YMRS)', category: 'Mania', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ymrs' },
    { title: 'Altman Self-Rating Mania Scale (ASRM)', category: 'Mania', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/asrm' },
    { title: 'Center for Epidemiologic Studies Depression Scale Revised (CESD-R)', category: 'Depression', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/cesdr' },
    { title: 'Fast alcohol screening test (FAST)', category: 'Substance', type: 'Minimum', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/fast' },
    { title: 'Alcohol Use Disorders Identification Test (AUDIT)', category: 'Substance', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/audit' },
    { title: 'Personal Drinking Questionnaire (SOCRATES 8A)', category: 'Substance', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/socrates8a' },
    { title: 'Personal Drug Use Questionnaire (SOCRATES 8D)', category: 'Substance', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/socrates8d' },
    { title: 'Brief Addiction Monitor (BAM)', category: 'Substance', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/bam' },
    { title: 'CAGE Substance Abuse Screening Tool', category: 'Substance', type: 'Minimum', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/cage' },
    { title: 'Rosenberg Self-Esteem Scale (RSS)', category: 'Self-Esteem', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/rss' },
    { title: 'Occupational Stress, Anxiety & Depression Inventory (OSADI)', category: 'Work', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/osadi' },
    { title: 'Fear Questionnaire (FQ)', category: 'Phobia', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/fq' },
    { title: 'Specific Phobia Of Vomiting Inventory (SPOVI)', category: 'Phobia', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/spovi' },
    { title: 'Hamilton Anxiety Rating Scale (HAM-A)', category: 'Anxiety', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/hama' },
    { title: 'IES-R Questionnaire', category: 'Trauma', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/iesr' },
    { title: 'LEC-5', category: 'Trauma', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/lec5' },
    { title: 'ACE Score', category: 'Trauma', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ace' },
    { title: 'PTSD Checklist – Civilian Version (PCL-C)', category: 'PTSD', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/pclc' },
    { title: 'PTSD Checklist – Military Version (PCL-M)', category: 'PTSD', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/pclm' },
    { title: 'Eating Disorder Diagnostic Scale (EDDS)', category: 'Eating', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/edds' },
    { title: 'Eating Attitudes Test (EAT-26)', category: 'Eating', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/eat26' },
    { title: 'Chalder Fatigue Scale', category: 'Physical', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/cfs' },
    { title: 'Client Service Receipt Inventory(CSRI)', category: 'Service', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/csri' },
    { title: 'COPD Assessment Test (CAT)', category: 'Physical', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/cat' },
    { title: 'Irritable Bowel Syndrome (IBS)', category: 'Physical', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ibs' },
    { title: 'Diabetes Distress Scale', category: 'Physical', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/dds' },
    { title: 'Insomnia Severity Index', category: 'Sleep', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/isi' },
    { title: 'Sleep Disturbance (DSM-5 Assessment)', category: 'Sleep', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/sleepdsm5' },
    { title: 'Epworth Sleepiness Scale', category: 'Sleep', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ess' },
    { title: 'Pittsburgh Sleep Quality Index (PSQI)', category: 'Sleep', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/psqi' },
    { title: 'Personality Inventory (DSM-5 Assessment)', category: 'Personality', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/personalitydsm5' },
    { title: 'Couples Satisfaction Index (CSI-8)', category: 'Relationships', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/csi8' },
    { title: 'Couples Satisfaction Index (CSI-32)', category: 'Relationships', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/csi32' },
    { title: 'Couples Conflict Questionnaire (CCQ)', category: 'Relationships', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ccq' },
    { title: 'Experiences and Close Relationships Scale (ECRS)', category: 'Relationships', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ecrs' },
    { title: 'Sexual Desire Inventory-2 (SDI-2)', category: 'Relationships', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/sdi2' },
    { title: 'Marital Status Inventory (MSI)', category: 'Relationships', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/msi' },
    { title: 'Repetitive Thoughts and Behaviours Scale (Children 11-17)', category: 'Child', type: 'Optional', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/rtbs' },
    { title: 'PHQ9-A Severity Measure for Depression Child Age 11–17', category: 'Child', type: 'Optional', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/phq9a' },
    { title: 'Informant Questionnaire on Cognitive Decline in the Elderly (IQCODE)', category: 'Elderly', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/iqcode' },
    { title: 'Geriatric Depression Scale for Elderly People', category: 'Elderly', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/gds' },
    { title: 'Adult ADHD Assessment', category: 'ADHD', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ada' },
    { title: 'Who Five Well Being Index (WHO-5)​', category: 'Wellbeing', type: 'Minimum', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/who' },
    { title: 'Brief Psychiatric Rating Scale (BPRS)', category: 'Psychiatric', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/bprs' },
    { title: 'Somatic Sympton (PHQ-15)', category: 'Physical', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/phq15' },
    { title: 'Adult ADHD Self-Report Scale', category: 'ADHD', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/asrs' },
    { title: 'Family Assessment Device - General Functioning Scale (FAD)', category: 'Family', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/fad' },
    { title: 'Roland Morris Disability Questionnaire', category: 'Physical', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/rmdq' },
    { title: 'RAND-36 (SF- 36) Health Status Questionnaire', category: 'Physical', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/rand36' },
    { title: 'CIWA-Ar for Alcohol Withdrawal', category: 'Substance', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ciwa-ar' },
    { title: 'Benzodiazepine Withdrawal Scale (CIWA-B)', category: 'Substance', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ciwa-b' },
    { title: 'Clinical Opiate Withdrawal Scale (COWS)', category: 'Substance', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/cows' },
    { title: 'The Michigan Alcohol Screening Test (MAST)', category: 'Substance', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/mast' },
    { title: 'Brief Dissociative Experiences Scale (BDES)', category: 'Trauma', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/bdes' },
    { title: 'PHQ8 (Depression)', category: 'Depression', type: 'Minimum', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/phq8' },
    { title: 'Bipolar Spectrum Diagnostic Scale (BSDS)', category: 'Bipolar', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/bsds' },
    { title: 'Health Anxiety Inventory (HAI-18)', category: 'Anxiety', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/hai' },
    { title: 'Severity Measure for Generalised Anxiety Disorder (SMGAD)', category: 'Anxiety', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/smgad' },
    { title: 'Panic Disorder Severity Scale (PDSS)', category: 'Anxiety', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/pdss' },
    { title: 'Penn State Worry Questionnaire (PSWQ)', category: 'Anxiety', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/pswq' },
    { title: 'Primary Care PTSD Screen for DSM-5 (PC-PTSD-5)', category: 'PTSD', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/pcptsd' },
    { title: 'Clinical Global Impressions (CGI-SELF)', category: 'Psychiatric', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/cgi-self' },
    { title: 'Somatic Symptom Severity Scale for Medically Unexplained Symptoms', category: 'Physical', type: 'Other', link: 'https://psychassessment.twinbrain.org/services/free-self-assessment/ssssmus' }
  ];

  const filterTypes = ['All', 'Minimum', 'Other', 'Optional'];

  const filteredQuestionnaires = questionnaires.filter(questionnaire => {
    const matchesSearch = questionnaire.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === 'All' || questionnaire.type === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg py-12 mb-12">
          <div className="text-center">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 flex items-center justify-center gap-3">
              <Gift className="h-10 w-10 text-green-600" />
              Free Psychological Questionnaires
            </h1>
            <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 mb-4">
              78 Free Psychological Questionnaires for Quick Assessment
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-6">
              Not interested in a half-hour one:on:one session? Don't worry. We made these questionnaires for you to instantly access your mental health.
            </p>
            <p className="text-md text-gray-600 max-w-4xl mx-auto">
              Filter Forms by Length or Type | Minutes for Anyone
            </p>
            <p className="text-md text-gray-600 max-w-4xl mx-auto mt-2">
              Whether you're interested in depression assessment or fast alcohol screening test. We listed 78 questionnaires which are accessible at one click away. Sort them manually or select the category to find all relevant forms.
            </p>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <p className="text-blue-800 text-sm leading-relaxed">
                This is a confidential free facility that gives you a chance to complete mood questionnaires. Scores are generated and can be emailed to your family doctor and other healthcare professionals chosen by you. The questionnaires are intended as a guide only to inform assessment and therapy with your qualified medical professional. High scores are not a confirmation of a diagnosis. Each free questionnaire is done online on this website. The time to complete questionnaire will vary among individuals.
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Search Questionnaires</h3>
          
          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search questionnaires..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter Buttons */}
          <div className="mb-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">Filter Questionnaires</h4>
            <div className="flex flex-wrap gap-3">
              {filterTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedFilter(type)}
                  className={`px-4 py-2 rounded-full transition-colors ${
                    selectedFilter === type 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {filteredQuestionnaires.length} of {questionnaires.length} questionnaires
          </p>
        </div>

        {/* Questionnaires Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredQuestionnaires.map((questionnaire, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <BookOpen className="h-6 w-6 text-blue-600 mr-3" />
                <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  {questionnaire.category}
                </span>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {questionnaire.title}
              </h3>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>Quick Assessment</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Target className="h-4 w-4 mr-2" />
                  <span>{questionnaire.type} Length</span>
                </div>
              </div>
              
              {questionnaire.link ? (
                <a 
                  href={questionnaire.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  Take Assessment
                  <ArrowRight className="h-4 w-4" />
                </a>
              ) : (
                <button 
                  onClick={() => navigate('/contact')}
                  className="w-full bg-gray-400 text-white py-2 px-4 rounded-lg hover:bg-gray-500 transition-colors flex items-center justify-center gap-2"
                >
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why Choose Our Free Assessments?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Instant results and insights',
              'No registration required',
              'Anonymous and confidential',
              'Evidence-based questionnaires',
              'Professional guidance included',
              'Mobile-friendly interface'
            ].map((benefit, index) => (
              <div key={index} className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-lg p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Need More Comprehensive Assessment?</h3>
          <p className="text-lg mb-6 opacity-90">
            For detailed psychological evaluations and personalized treatment plans, 
            consider our professional paid assessment services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              View Paid Assessments
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeAssessmentPage;